import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { masterService } from '../../services/api';
import { Button, Modal, Badge, Skeleton, EmptyState, Toast, Tab, Input } from '../../components/ui';
import { thStyle, tdStyle, iconBtnStyle } from '../../utils/tableStyles';

const TABS = [
  { key: 'lead-sources', label: 'Lead Sources' },
  { key: 'lead-statuses', label: 'Lead Statuses' },
  { key: 'call-feedbacks', label: 'Call Feedbacks' },
  { key: 'retail-ous', label: 'Retail OUs' },
];

const MasterData = () => {
  const { dark } = useTheme();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('masters', 'create') || hasPermission('master', 'create');
  const canEdit = hasPermission('masters', 'update') || hasPermission('master', 'update');
  const canDelete = hasPermission('masters', 'delete') || hasPermission('master', 'delete');

  const [tab, setTab] = useState('lead-sources');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', color: '#3B82F6', description: '', order: 0, isFinal: false });

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      let resp;
      if (tab === 'lead-sources') resp = await masterService.getLeadSources();
      else if (tab === 'lead-statuses') resp = await masterService.getLeadStatuses();
      else if (tab === 'call-feedbacks') resp = await masterService.getCallFeedbacks();
      else resp = await masterService.getRetailOUs();
      setItems(resp.data.data || []);
    } catch { setToast({ msg: 'Error loading data', type: 'error' }); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { fetch(); }, [fetch]);

  const openCreate = () => { setEditItem(null); setForm({ name: '', code: '', color: '#3B82F6', description: '', order: 0, isFinal: false }); setFormOpen(true); };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({ name: item.name || '', code: item.code || '', color: item.color || '#3B82F6', description: item.description || '', order: item.order || 0, isFinal: item.isFinal || false });
    setFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (tab === 'lead-sources') {
        if (editItem) await masterService.updateLeadSource(editItem._id, form);
        else await masterService.createLeadSource(form);
      } else if (tab === 'lead-statuses') {
        if (editItem) await masterService.updateLeadStatus(editItem._id, form);
        else await masterService.createLeadStatus(form);
      } else if (tab === 'call-feedbacks') {
        if (editItem) await masterService.updateCallFeedback(editItem._id, form);
        else await masterService.createCallFeedback(form);
      } else {
        if (editItem) await masterService.updateRetailOU(editItem._id, form);
        else await masterService.createRetailOU(form);
      }
      setToast({ msg: editItem ? 'Updated successfully' : 'Created successfully', type: 'success' });
      setFormOpen(false);
      fetch();
    } catch (err) { setToast({ msg: err.response?.data?.message || 'Error', type: 'error' }); }
  };

  const handleDelete = async () => {
    try {
      if (tab === 'lead-sources') await masterService.deleteLeadSource(deleteId);
      else if (tab === 'lead-statuses') await masterService.deleteLeadStatus(deleteId);
      else if (tab === 'call-feedbacks') await masterService.deleteCallFeedback(deleteId);
      else await masterService.deleteRetailOU(deleteId);
      setToast({ msg: 'Deleted', type: 'success' });
      setDeleteId(null);
      fetch();
    } catch (err) { setToast({ msg: err.response?.data?.message || 'Error deleting', type: 'error' }); }
  };

  const hasColor = ['lead-sources', 'lead-statuses', 'call-feedbacks'].includes(tab);
  const hasCode  = ['lead-sources', 'lead-statuses', 'call-feedbacks'].includes(tab);
  const hasOrder = tab === 'lead-statuses';
  const hasFinal = tab === 'lead-statuses';

  const card = { background: dark ? '#0F172A' : '#fff', border: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}`, borderRadius: 16, overflow: 'hidden' };
  const f = (field) => ({ value: form[field], onChange: e => setForm({ ...form, [field]: e.target.value }) });

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', margin: 0 }}>Master Data</h1>
        {canCreate && <Button icon={Plus} onClick={openCreate}>Add {TABS.find(t => t.key === tab)?.label.slice(0, -1)}</Button>}
      </div>

      <div style={card}>
        <div style={{ padding: '16px 20px 0', borderBottom: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}` }}>
          <Tab tabs={TABS} active={tab} onChange={k => { setTab(k); }} />
        </div>

        <div style={{ overflowX: 'auto' }}>
          {loading ? <div style={{ padding: 20 }}><Skeleton rows={5} cols={3} /></div> :
            items.length === 0 ? <div style={{ padding: 40 }}><EmptyState title="No items found" /></div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                  <tr>
                    <th style={thStyle(dark)}>Name</th>
                    {hasCode && <th style={thStyle(dark)}>Code</th>}
                    {hasColor && <th style={thStyle(dark)}>Color</th>}
                    {hasOrder && <th style={thStyle(dark)}>Order</th>}
                    {hasFinal && <th style={thStyle(dark)}>Final Status</th>}
                    <th style={thStyle(dark)}>Description</th>
                    <th style={thStyle(dark)}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item._id} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                      <td style={{ ...tdStyle(dark), fontWeight: 600 }}>
                        {hasColor && <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: item.color || '#6B7280', marginRight: 8 }} />}
                        {item.name}
                      </td>
                      {hasCode && <td style={{ ...tdStyle(dark), fontFamily: 'monospace', fontSize: 11 }}>{item.code || '—'}</td>}
                      {hasColor && <td style={tdStyle(dark)}><Badge color={item.color || '#6B7280'}>{item.color || '—'}</Badge></td>}
                      {hasOrder && <td style={tdStyle(dark)}>{item.order ?? '—'}</td>}
                      {hasFinal && <td style={tdStyle(dark)}>{item.isFinal ? <Badge color="#10B981">Yes</Badge> : <Badge color="#6B7280">No</Badge>}</td>}
                      <td style={{ ...tdStyle(dark), color: dark ? '#64748B' : '#94A3B8' }}>{item.description || '—'}</td>
                      <td style={tdStyle(dark)}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {canEdit && <button style={iconBtnStyle(dark)} onClick={() => openEdit(item)}><Pencil size={14} /></button>}
                          {canDelete && <button style={{ ...iconBtnStyle(dark), color: '#EF4444' }} onClick={() => setDeleteId(item._id)}><Trash2 size={14} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>

      <Modal open={formOpen} onClose={() => setFormOpen(false)} title={editItem ? 'Edit' : 'Add'} size="sm">
        <form onSubmit={handleSubmit}>
          <Input label="Name *" {...f('name')} placeholder="Enter name" />
          {hasCode && <Input label="Code" {...f('code')} placeholder="e.g. NEW_LEAD" />}
          {hasColor && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: dark ? '#94A3B8' : '#64748B', display: 'block', marginBottom: 6 }}>Color</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} style={{ width: 40, height: 36, borderRadius: 6, border: 'none', cursor: 'pointer' }} />
                <span style={{ fontSize: 12, fontFamily: 'monospace', color: dark ? '#94A3B8' : '#64748B' }}>{form.color}</span>
              </div>
            </div>
          )}
          {hasOrder && <Input label="Order" type="number" {...f('order')} placeholder="Display order" />}
          {hasFinal && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isFinal} onChange={e => setForm({ ...form, isFinal: e.target.checked })} />
              <span style={{ color: dark ? '#CBD5E1' : '#334155' }}>Final/terminal status</span>
            </label>
          )}
          <Input label="Description" {...f('description')} placeholder="Optional description" />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <Button variant="outline" type="button" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button type="submit">{editItem ? 'Save' : 'Create'}</Button>
          </div>
        </form>
      </Modal>

      <Modal open={!!deleteId} onClose={() => setDeleteId(null)} title="Confirm Delete" size="sm">
        <p style={{ fontSize: 14, color: dark ? '#CBD5E1' : '#334155', margin: '0 0 20px' }}>Are you sure?</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete}>Delete</Button>
        </div>
      </Modal>
    </div>
  );
};

export default MasterData;
